import React from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import type { EstadoDeCobro } from "../../lib/estadoDeCobro";
import { useAperturaAnimada } from "../../hooks/useAperturaAnimada";
import { AvisoDeCobro } from "./AvisoDeCobro";

/**
 * El aviso de cobro rechazado en un modal: lo abre el botón "Pago rechazado" de la sidebar de
 * escritorio, en lugar de "Ver planes" (ADR-2026-10-26-cobros-rechazados-en-la-plataforma). Muestra
 * exactamente lo mismo que el cartel de la home: es `AvisoDeCobro` en su variante `modal`.
 *
 * Portal al `body`: el panel de contenido del shell es `md:fixed` y un overlay adentro quedaría
 * debajo de la sidebar (skill interfaz-de-atlas).
 */
const SALIDA_MS = 150;

export const ModalDeCobro: React.FC<{
  estado: EstadoDeCobro;
  abierto: boolean;
  onCerrar: () => void;
}> = ({ estado, abierto, onCerrar }) => {
  const { montado, visible } = useAperturaAnimada(abierto, SALIDA_MS);

  React.useEffect(() => {
    if (!abierto) return;
    const alTeclear = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCerrar();
    };
    window.addEventListener("keydown", alTeclear);
    return () => window.removeEventListener("keydown", alTeclear);
  }, [abierto, onCerrar]);

  if (!montado) return null;

  return createPortal(
    <div
      className={`fixed inset-0 z-[200] flex items-center justify-center bg-black/30 px-4 transition-opacity ease-out ${
        visible ? "opacity-100 duration-300" : "opacity-0 duration-150"
      }`}
      onClick={onCerrar}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Estado del cobro de tu suscripción"
        onClick={(e) => e.stopPropagation()}
        className={`relative w-full max-w-[440px] rounded-lg bg-white p-5 shadow-xl transition-[opacity,transform,filter] ease-out ${
          visible
            ? "opacity-100 duration-300 motion-safe:translate-y-0 motion-safe:blur-0"
            : "opacity-0 duration-150 motion-safe:translate-y-3 motion-safe:blur-[4px]"
        }`}
      >
        <button
          type="button"
          onClick={onCerrar}
          aria-label="Cerrar"
          className="absolute right-3 top-3 rounded-sm p-1.5 text-brand-black/40 transition-colors hover:text-brand-black/70"
        >
          <X className="h-4 w-4" />
        </button>
        <AvisoDeCobro estado={estado} variante="modal" onNavegar={onCerrar} />
      </div>
    </div>,
    document.body,
  );
};
